import { useDelete, useList, useUpdate } from '@refinedev/core';

import { ui } from '../ui';

/**
 * Fila de moderação (história 12).
 *
 * Como funciona, e por que a fila existe: o comentário some **para quem denunciou** na hora, e
 * some **para todos** com duas denúncias de pessoas diferentes (`report_hide_threshold`). Quem
 * esconde é o gatilho no banco, não esta tela. O que chega aqui é o que já está escondido
 * esperando decisão humana — o fundador se comprometeu com revisão em até 24 h (`docs/01`).
 *
 * As duas ações são opostas e ambas definitivas:
 *   · **Manter** devolve o comentário ao ar e apaga as denúncias, senão a terceira denúncia o
 *     esconderia de novo e a decisão não teria valido nada.
 *   · **Remover** deixa o comentário escondido para sempre.
 *
 * O admin enxerga comentário escondido porque a política de leitura tem `is_admin()`; ninguém
 * mais vê.
 */
type Denuncia = {
  id: string;
  comment_id: string;
  reason: string;
  created_at: string;
  comments: { id: string; body: string; status: string; episode_id: string } | null;
};

/**
 * Os valores são os do banco (`comment_reports.reason`), que são em português:
 * `check (reason in ('ofensa','assedio','spam','sexual','spoiler','outro'))`.
 * Eu tinha escrito as chaves em inglês e a fila mostraria o código cru em vez do motivo.
 */
const MOTIVOS: Record<string, string> = {
  ofensa: 'Ofensa',
  assedio: 'Assédio',
  spam: 'Spam ou golpe',
  sexual: 'Sexual',
  spoiler: 'Spoiler',
  outro: 'Outro',
};

export function Moderacao() {
  const { query, result } = useList<Denuncia>({
    resource: 'comment_reports',
    meta: { select: 'id,comment_id,reason,created_at,comments(id,body,status,episode_id)' },
    sorters: [{ field: 'created_at', order: 'asc' }],
    pagination: { pageSize: 100 },
  });

  const { mutate: atualizar } = useUpdate();
  const { mutate: apagar } = useDelete();

  const denuncias = result.data;

  /** Agrupa por comentário: duas denúncias do mesmo texto são uma decisão, não duas. */
  const porComentario = new Map<
    string,
    { denuncias: Denuncia[]; comentario: Denuncia['comments'] }
  >();
  for (const d of denuncias) {
    const atual = porComentario.get(d.comment_id);
    if (atual) atual.denuncias.push(d);
    else porComentario.set(d.comment_id, { denuncias: [d], comentario: d.comments });
  }

  function manter(commentId: string, ids: string[]) {
    atualizar(
      { resource: 'comments', id: commentId, values: { status: 'visible' } },
      {
        onSuccess: () => {
          // Sem apagar as denúncias, a próxima esconderia o comentário de novo.
          let pendentes = ids.length;
          for (const id of ids) {
            apagar(
              { resource: 'comment_reports', id, successNotification: false },
              {
                onSettled: () => {
                  pendentes -= 1;
                  if (pendentes === 0) void query.refetch();
                },
              },
            );
          }
        },
      },
    );
  }

  function remover(commentId: string) {
    atualizar(
      { resource: 'comments', id: commentId, values: { status: 'removed' } },
      { onSuccess: () => void query.refetch() },
    );
  }

  return (
    <>
      <h1 style={ui.h1}>Moderação</h1>

      <div style={ui.aviso}>
        O comentário some para todos com <strong>duas denúncias</strong> de pessoas diferentes, e
        isso acontece no banco, na hora. O que está aqui já está escondido e espera decisão. Manter
        devolve ao ar e apaga as denúncias; remover deixa escondido para sempre.
      </div>

      {query.isLoading && <p style={ui.dim}>Carregando…</p>}

      {query.error && <p style={ui.erro}>Não deu para ler a fila: {query.error.message}</p>}

      {!query.isLoading && porComentario.size === 0 && (
        <p style={ui.dim}>Nada na fila. Nenhum comentário aguardando decisão.</p>
      )}

      {[...porComentario.entries()].map(([commentId, grupo]) => (
        <article
          key={commentId}
          style={{
            border: `1px solid ${'rgba(255,255,255,.10)'}`,
            borderRadius: 12,
            padding: 16,
            marginBottom: 12,
          }}
        >
          <p style={{ margin: '0 0 8px', fontSize: 15, lineHeight: 1.5 }}>
            {grupo.comentario?.body ?? <span style={ui.dim}>(comentário apagado pelo autor)</span>}
          </p>

          <p style={{ ...ui.dim, fontSize: 13, margin: '0 0 12px' }}>
            {grupo.denuncias.length} {grupo.denuncias.length === 1 ? 'denúncia' : 'denúncias'} ·{' '}
            {grupo.denuncias.map((d) => MOTIVOS[d.reason] ?? d.reason).join(', ')} · situação:{' '}
            {grupo.comentario?.status ?? '—'}
          </p>

          <div style={ui.linhaBotoes}>
            <button
              type="button"
              style={ui.botaoSecundario}
              onClick={() =>
                manter(
                  commentId,
                  grupo.denuncias.map((d) => d.id),
                )
              }
            >
              Manter no ar
            </button>
            <button type="button" style={ui.botaoPerigo} onClick={() => remover(commentId)}>
              Remover
            </button>
          </div>
        </article>
      ))}
    </>
  );
}
